import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';

// Google Maps Component
const GoogleMap = () => {
  const mapRef = React.useRef(null);
  const [map, setMap] = React.useState(null);
  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    // Charger l'API Google Maps
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAOApQ6tmtN6OmArV2J1PJhyb2X4VzGhpA&libraries=places`;
      script.async = true;
      script.onload = () => setIsLoaded(true);
      document.head.appendChild(script);
    } else {
      setIsLoaded(true);
    }
  }, []);

  React.useEffect(() => {
    if (isLoaded && mapRef.current && !map) {
      // Utiliser le service de géocodage pour obtenir les coordonnées exactes
      const geocoder = new window.google.maps.Geocoder();
      const address = "250 bis boulevard Jean Jaurès, 92100 Boulogne Billancourt, France";
      
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          
          const googleMap = new window.google.maps.Map(mapRef.current, {
            center: location,
            zoom: 16,
            styles: [
              {
                "featureType": "all",
                "elementType": "geometry.fill",
                "stylers": [{"color": "#f5f5f5"}]
              },
              {
                "featureType": "water",
                "elementType": "geometry.fill",
                "stylers": [{"color": "#dbeafe"}]
              }
            ]
          });

          // Ajouter un marqueur à l'adresse exacte
          const marker = new window.google.maps.Marker({
            position: location,
            map: googleMap,
            title: "Fenêtre & Co. - 250 bis boulevard Jean Jaurès, 92100 Boulogne Billancourt",
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: "#fffbad",
              fillOpacity: 1,
              strokeColor: "#000",
              strokeWeight: 2
            }
          });

          // Fenêtre d'information
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 10px; font-family: Arial, sans-serif;">
                <h3 style="color: #1f2937; margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">Fenêtre & Co.</h3>
                <p style="margin: 4px 0; color: #4b5563; font-size: 14px;">📍 250 bis boulevard Jean Jaurès</p>
                <p style="margin: 4px 0; color: #4b5563; font-size: 14px;">92100 Boulogne Billancourt</p>
                <p style="margin: 4px 0; color: #4b5563; font-size: 14px;">📞 07 56 93 03 67</p>
                <p style="margin: 8px 0 0 0; color: #059669; font-size: 12px; font-weight: bold;">✨ Déplacement gratuit en Île-de-France</p>
              </div>
            `
          });

          marker.addListener('click', () => {
            infoWindow.open(googleMap, marker);
          });

          setMap(googleMap);
        } else {
          console.error('Geocoding failed:', status);
          // Fallback vers des coordonnées approximatives si le géocodage échoue
          const fallbackLocation = { lat: 48.8314, lng: 2.2426 };
          const googleMap = new window.google.maps.Map(mapRef.current, {
            center: fallbackLocation,
            zoom: 16
          });
          setMap(googleMap);
        }
      });
    }
  }, [isLoaded, map]);

  return (
    <div className="relative">
      <div 
        ref={mapRef} 
        className="w-full h-96 rounded-lg"
        style={{ minHeight: '400px' }}
      />
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center border-2 border-blue-200">
          <div className="text-center">
            <div className="text-6xl mb-4">🗺️</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Chargement de la carte...
            </h3>
            <p className="text-gray-600 text-lg">
              Boulogne Billancourt
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Breadcrumb Component
const Breadcrumb = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const breadcrumbNameMap = {
    'services': 'Services',
    'fenetres-pvc': 'Fenêtres en PVC',
    'fenetres-aluminium': 'Fenêtres en Aluminium',
    'fenetres-bois': 'Fenêtres en Bois',
    'baies-vitrees': 'Baies Vitrées',
    'partenariat': 'Partenariat',
    'a-propos': 'À Propos',
    'contact': 'Contact'
  };

  if (location.pathname === '/') {
    return null; // Pas de breadcrumb sur la page d'accueil
  }

  return (
    <nav className="bg-gray-50 py-4 border-b mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ol className="flex items-center space-x-2 text-sm">
          <li>
            <Link to="/" className="text-gray-500 hover:text-yellow-600 transition-colors">
              🏠 Accueil
            </Link>
          </li>
          {pathnames.map((name, index) => {
            const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
            const isLast = index === pathnames.length - 1;
            const displayName = breadcrumbNameMap[name] || name;

            return (
              <li key={name} className="flex items-center">
                <span className="mx-2 text-gray-400">/</span>
                {isLast ? (
                  <span className="text-gray-900 font-semibold">
                    {displayName}
                  </span>
                ) : (
                  <Link 
                    to={routeTo} 
                    className="text-gray-500 hover:text-yellow-600 transition-colors"
                  >
                    {displayName}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

// Navigation Component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed w-full top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3">
            <img src="/logo2.svg" alt="Fenêtre & Co." className="w-24 h-24 object-contain" />
            <span className="text-xl font-bold text-gray-900">Fenêtre & Co.</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className={`nav-link ${isActive('/') ? 'text-yellow-400' : 'text-gray-700'}`}>
              Accueil
            </Link>
            <Link to="/services" className={`nav-link ${isActive('/services') ? 'text-yellow-400' : 'text-gray-700'}`}>
              Services
            </Link>
            <Link to="/partenariat" className={`nav-link ${isActive('/partenariat') ? 'text-yellow-400' : 'text-gray-700'}`}>
              Partenariat
            </Link>
            <Link to="/a-propos" className={`nav-link ${isActive('/a-propos') ? 'text-yellow-400' : 'text-gray-700'}`}>
              À Propos
            </Link>
            <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'text-yellow-400' : 'text-gray-700'}`}>
              Contact
            </Link>
            <Link to="/contact" className="btn-primary">
              Devis Gratuit
            </Link>
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span className="sr-only">Menu</span>
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`bg-gray-900 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${
                isMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'
              }`}></span>
              <span className={`bg-gray-900 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${
                isMenuOpen ? 'opacity-0' : 'opacity-100'
              }`}></span>
              <span className={`bg-gray-900 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${
                isMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'
              }`}></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link to="/" className="mobile-nav-link">Accueil</Link>
            <Link to="/services" className="mobile-nav-link">Services</Link>
            <Link to="/partenariat" className="mobile-nav-link">Partenariat</Link>
            <Link to="/a-propos" className="mobile-nav-link">À Propos</Link>
            <Link to="/contact" className="mobile-nav-link">Contact</Link>
            <Link to="/contact" className="btn-primary w-full text-center mt-4">
              Devis Gratuit
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <img src="/logo2.svg" alt="Fenêtre & Co." className="w-24 h-24 object-contain" />
              <span className="text-xl font-bold">Fenêtre & Co.</span>
            </div>
            <p className="text-gray-300 mb-4">
              Spécialistes en installation de fenêtres depuis 1989. Trois générations d'artisans au service de votre confort.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61578792684278" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors">
                <span className="sr-only">Facebook</span>
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Services</h3>
            <ul className="space-y-2 text-gray-300">
              <li><Link to="/services/fenetres-pvc" className="hover:text-white transition-colors">Fenêtres PVC</Link></li>
              <li><Link to="/services/fenetres-aluminium" className="hover:text-white transition-colors">Fenêtres Aluminium</Link></li>
              <li><Link to="/services/fenetres-bois" className="hover:text-white transition-colors">Fenêtres Bois</Link></li>
              <li><Link to="/services/baies-vitrees" className="hover:text-white transition-colors">Baies Vitrées</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact</h3>
            <ul className="space-y-2 text-gray-300">
              <li>📞 07 56 93 03 67</li>
              <li>📧 rudyo@ddr-solutions.com</li>
              <li>🕒 Lun-Ven: 09h00-20h00</li>
              <li>📍 Boulogne Billancourt, Île-de-France</li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-300">
          <p>&copy; 2024 Fenêtre & Co. Tous droits réservés.</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const Home = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    projectType: '',
    message: ''
  });

  const [scrollY, setScrollY] = useState(0);

  // Effet parallax
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Préparer les données pour Zapier
    const zapierData = {
      type: "Demande de devis",
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      projectType: formData.projectType,
      message: formData.message,
      timestamp: new Date().toISOString(),
      source: "Page d'accueil"
    };

    console.log('Envoi vers Zapier:', zapierData);

    // Méthode 1: Via une image invisible (toujours fonctionnel)
    const zapierUrl = 'https://hooks.zapier.com/hooks/catch/7451293/u2mcxu6/?' + 
      new URLSearchParams({
        type: zapierData.type,
        firstName: zapierData.firstName,
        lastName: zapierData.lastName,
        phone: zapierData.phone,
        email: zapierData.email,
        projectType: zapierData.projectType,
        message: zapierData.message,
        timestamp: zapierData.timestamp,
        source: zapierData.source
      }).toString();

    // Créer une image invisible pour déclencher la requête
    const img = new Image();
    img.src = zapierUrl;
    
    console.log('URL Zapier:', zapierUrl);

    // Méthode 2: Via le backend (backup)
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zapier-webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(zapierData),
      });

      if (response.ok) {
        console.log('Succès via backend');
      } else {
        console.log('Échec backend, mais image envoyée');
      }
    } catch (error) {
      console.log('Erreur backend, mais image envoyée:', error);
    }

    alert('Merci pour votre demande ! Nous vous recontacterons dans les 24h.');
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      projectType: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background video avec effet parallax */}
        <video 
          className="absolute inset-0 w-full h-full object-cover will-change-transform"
          style={{
            transform: `translateY(${scrollY * 0.5}px) scale(${1 + scrollY * 0.0005})`
          }}
          autoPlay 
          muted 
          loop 
          playsInline
        >
          <source src="/Design sans titre (2).mp4" type="video/mp4" />
          {/* Fallback image if video doesn't load */}
          <img 
            src="https://images.unsplash.com/photo-1706459492989-60e252230fca"
            alt="Fenêtres"
            className="w-full h-full object-cover"
          />
        </video>
        
        {/* Overlay avec dégradé dynamique */}
        <div 
          className="absolute inset-0 will-change-transform"
          style={{
            background: `linear-gradient(135deg, rgba(0,0,0,${0.2 + scrollY * 0.001}) 0%, rgba(255,251,173,${0.1 + scrollY * 0.0005}) 100%)`,
            transform: `translateY(${scrollY * 0.2}px)`
          }}
        ></div>

        {/* Éléments flottants 3D */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Particules flottantes */}
          <div 
            className="absolute top-20 left-10 w-4 h-4 bg-yellow-300 rounded-full opacity-60 will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.3}px) translateX(${Math.sin(scrollY * 0.01) * 20}px) rotateZ(${scrollY * 0.5}deg)`
            }}
          ></div>
          <div 
            className="absolute top-40 right-20 w-6 h-6 bg-white rounded-full opacity-40 will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.6}px) translateX(${Math.cos(scrollY * 0.008) * 30}px) rotateZ(${scrollY * -0.3}deg)`
            }}
          ></div>
          <div 
            className="absolute top-60 left-1/3 w-3 h-3 bg-yellow-400 rounded-full opacity-50 will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.4}px) translateX(${Math.sin(scrollY * 0.012) * 25}px) rotateZ(${scrollY * 0.7}deg)`
            }}
          ></div>
          <div 
            className="absolute bottom-40 right-10 w-5 h-5 bg-white rounded-full opacity-30 will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.8}px) translateX(${Math.cos(scrollY * 0.015) * 15}px) rotateZ(${scrollY * -0.5}deg)`
            }}
          ></div>
          
          {/* Formes géométriques */}
          <div 
            className="absolute top-32 right-1/4 w-8 h-8 border-2 border-yellow-300 opacity-40 will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.5}px) rotateZ(${scrollY * 0.8}deg) scale(${1 + Math.sin(scrollY * 0.01) * 0.2})`
            }}
          ></div>
          <div 
            className="absolute bottom-32 left-1/4 w-6 h-6 border-2 border-white opacity-30 will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.7}px) rotateZ(${scrollY * -0.6}deg) scale(${1 + Math.cos(scrollY * 0.008) * 0.3})`
            }}
          ></div>
        </div>
        
        {/* Contenu principal avec parallax */}
        <div 
          className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-black will-change-transform"
          style={{
            transform: `translateY(${scrollY * -0.1}px) scale(${1 - scrollY * 0.0002})`
          }}
        >
          <h1 
            className="text-5xl md:text-7xl font-bold mb-8 leading-tight will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.15}px)`,
              textShadow: `0 4px 8px rgba(0,0,0,${0.3 + scrollY * 0.001})`
            }}
          >
            Fenêtres sur mesure
          </h1>
          <p 
            className="text-xl md:text-2xl mb-8 text-gray-900 will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.05}px)`,
              textShadow: `0 2px 4px rgba(0,0,0,${0.2 + scrollY * 0.0005})`
            }}
          >
            Installations professionnelles en Île-de-France
          </p>
          <div 
            className="bg-yellow-300 text-black rounded-2xl p-6 inline-block mb-8 will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.08}px) rotateX(${scrollY * 0.02}deg)`,
              boxShadow: `0 8px 32px rgba(255,251,173,${0.4 + scrollY * 0.001})`
            }}
          >
            <p className="text-2xl font-bold">
              À partir de <span className="text-4xl">499€</span>
            </p>
          </div>
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center will-change-transform"
            style={{
              transform: `translateY(${scrollY * -0.12}px)`
            }}
          >
            <a href="#devis" className="btn-primary text-lg px-8 py-4 flex items-center justify-center">
              Devis Gratuit
            </a>
            <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className="font-semibold">07 56 93 03 67</span>
            </a>
          </div>
        </div>
        
        {/* Scroll Indicator avec parallax */}
        <div 
          className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20 will-change-transform"
          style={{
            transform: `translateX(-50%) translateY(${scrollY * -0.3}px)`,
            opacity: Math.max(0, 1 - scrollY * 0.003)
          }}
        >
          <div className="flex flex-col items-center animate-bounce cursor-pointer" onClick={() => {
            document.querySelector('#testimonials')?.scrollIntoView({ behavior: 'smooth' });
          }}>
            <p className="text-white mb-3 text-base font-semibold drop-shadow-lg">Découvrir nos services</p>
            <div className="w-8 h-12 border-3 border-white rounded-full flex justify-center shadow-lg bg-black/20 backdrop-blur-sm">
              <div className="w-1.5 h-4 bg-white rounded-full mt-2 animate-pulse shadow-sm"></div>
            </div>
            <svg 
              className="w-8 h-8 text-white mt-3 drop-shadow-lg" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={3} 
                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
              />
            </svg>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-b from-white via-gray-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600">
              Découvrez les avis de nos clients satisfaits
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="testimonial-card">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-300 rounded-full flex items-center justify-center text-black font-bold">
                  M
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Marie Dubois</h4>
                  <p className="text-gray-600">Vincennes (94)</p>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Installation parfaite de nos fenêtres PVC. Équipe professionnelle et travail soigné. Nos factures de chauffage ont diminué de 30% !"
              </p>
              <div className="flex text-yellow-400 mt-4">
                ⭐⭐⭐⭐⭐
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-300 rounded-full flex items-center justify-center text-black font-bold">
                  J
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Jean Martin</h4>
                  <p className="text-gray-600">Créteil (94)</p>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Excellent rapport qualité-prix. Fenêtres en bois sur mesure livrées dans les délais. Service client très réactif."
              </p>
              <div className="flex text-yellow-400 mt-4">
                ⭐⭐⭐⭐⭐
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-yellow-300 rounded-full flex items-center justify-center text-black font-bold">
                  S
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-gray-900">Sophie Leroy</h4>
                  <p className="text-gray-600">Nogent-sur-Marne (94)</p>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Entreprise familiale de confiance. Pose de baies vitrées aluminium impeccable. Je recommande vivement !"
              </p>
              <div className="flex text-yellow-400 mt-4">
                ⭐⭐⭐⭐⭐
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 via-yellow-200 to-yellow-300 text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Zone d'intervention
          </h2>
          <div className="bg-black/10 backdrop-blur-md rounded-2xl p-8 inline-block">
            <p className="text-xl mb-4">
              🗺️ Intervention rapide en Île-de-France et toute la grande couronne
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>Paris (75)</div>
              <div>Seine-et-Marne (77)</div>
              <div>Yvelines (78)</div>
              <div>Essonne (91)</div>
              <div>Hauts-de-Seine (92)</div>
              <div>Seine-Saint-Denis (93)</div>
              <div>Val-de-Marne (94)</div>
              <div>Val-d'Oise (95)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantees Section */}
      <section id="garanties" className="py-20 bg-gradient-to-b from-yellow-300 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos garanties
            </h2>
            <p className="text-xl text-gray-600">
              Votre tranquillité d'esprit est notre priorité
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="guarantee-card">
              <div className="text-4xl mb-4">🔧</div>
              <h3 className="text-xl font-semibold mb-2">Professionnels qualifiés</h3>
              <p className="text-gray-600">
                Nos artisans sont certifiés et formés aux dernières techniques d'installation
              </p>
            </div>
            
            <div className="guarantee-card">
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="text-xl font-semibold mb-2">Garantie décennale</h3>
              <p className="text-gray-600">
                Toutes nos installations sont couvertes par une garantie décennale
              </p>
            </div>
            
            <div className="guarantee-card">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-semibold mb-2">SAV réactif</h3>
              <p className="text-gray-600">
                Service après-vente disponible 6j/7 pour toute intervention
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section - Moved before Footer */}
      <section id="devis" className="py-20 bg-gradient-to-b from-white via-gray-50 to-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Demandez votre devis gratuit
            </h2>
            <p className="text-xl text-gray-600">
              Réponse garantie sous 24h • Déplacement offert en Île-de-France
            </p>
          </div>
          
          <div className="bg-yellow-300 rounded-2xl p-8 shadow-lg">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label className="form-label-dark">Prénom *</label>
                <input 
                  type="text" 
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label-dark">Nom *</label>
                <input 
                  type="text" 
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label-dark">Téléphone *</label>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label-dark">Email *</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group md:col-span-2">
                <label className="form-label-dark">Type de projet *</label>
                <select 
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="form-input"
                >
                  <option value="">Sélectionnez votre projet</option>
                  <option value="remplacement">Remplacement</option>
                  <option value="renovation">Rénovation</option>
                  <option value="construction">Construction neuve</option>
                </select>
              </div>
              
              <div className="form-group md:col-span-2">
                <label className="form-label-dark">Message</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="form-input"
                  placeholder="Décrivez votre projet..."
                ></textarea>
              </div>
              
              <div className="md:col-span-2">
                <button type="submit" className="btn-primary w-full text-lg py-4">
                  Envoyer ma demande
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

// Fenêtres PVC Page Component
const FenetresPVC = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-100 to-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Fenêtres en PVC : Performance et Économies
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Découvrez nos fenêtres en PVC haute performance, alliant isolation thermique exceptionnelle, durabilité et design moderne pour votre habitat en Île-de-France.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                  Devis Gratuit
                </Link>
                <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4">
                  📞 07 56 93 03 67
                </a>
              </div>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              {/* Vidéo Fenêtres PVC */}
              <video 
                className="w-full h-auto rounded-lg" 
                autoPlay 
                muted 
                loop 
                playsInline
                poster="/services/9.png"
              >
                <source src="/realisations/fenetres 3d.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Article SEO */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Pourquoi Choisir les Fenêtres en PVC ?</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres en PVC</strong> représentent aujourd'hui la solution la plus plébiscitée par les propriétaires français souhaitant allier performance énergétique, durabilité et budget maîtrisé. Chez Fenêtre & Co., spécialiste de l'<strong>installation de fenêtres en PVC</strong> en Île-de-France, nous vous accompagnons dans votre projet de rénovation ou de construction neuve avec des produits de qualité supérieure.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Les Avantages des Fenêtres PVC</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres en PVC</strong> offre de nombreux avantages qui en font le choix privilégié de millions de foyers. Le PVC (Polychlorure de Vinyle) est un matériau moderne qui a révolutionné le marché de la menuiserie. Sa composition chimique stable lui confère une résistance exceptionnelle aux intempéries, aux UV et aux variations climatiques d'Île-de-France.
            </p>

            <div className="bg-yellow-300 p-6 rounded-2xl my-8">
              <h4 className="text-xl font-bold text-black mb-4">🎯 Isolation Thermique Exceptionnelle</h4>
              <p className="text-black">
                Nos <strong>fenêtres PVC</strong> atteignent des performances d'isolation remarquables avec des coefficients Uw pouvant descendre jusqu'à 0,9 W/m².K, vous garantissant des économies d'énergie substantielles.
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Performance Énergétique et Économies</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres PVC</strong> permet de réaliser jusqu'à 15% d'économies sur vos factures de chauffage. Grâce à leur conception multichambre et à l'utilisation de vitrages performants, nos <strong>fenêtres en PVC</strong> créent une barrière thermique efficace entre l'intérieur et l'extérieur de votre habitation.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Le double vitrage standard équipant nos <strong>fenêtres PVC</strong> peut être remplacé par du triple vitrage pour les projets nécessitant une isolation renforcée. Cette solution est particulièrement recommandée pour les expositions nord ou dans les zones particulièrement bruyantes d'Île-de-France.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Durabilité et Entretien des Fenêtres PVC</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres en PVC</strong> se distinguent par leur longévité exceptionnelle. Contrairement aux idées reçues, le PVC moderne ne jaunit plus et conserve son aspect d'origine pendant des décennies. Nos <strong>fenêtres PVC</strong> sont garanties 10 ans et peuvent facilement durer 30 à 40 ans avec un entretien minimal.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'entretien des <strong>fenêtres PVC</strong> se limite à un nettoyage régulier avec de l'eau savonneuse. Pas de peinture, pas de traitement particulier : le PVC conserve naturellement ses propriétés et son esthétique au fil des années.
            </p>

            <div className="bg-gray-50 p-6 rounded-2xl my-8">
              {/* Vidéo réalisations Fenêtres PVC */}
              <video 
                className="w-full h-auto rounded-lg mb-4" 
                autoPlay
                muted
                loop
                playsInline
                poster="/services/9.png"
              >
                <source src="/realisations/Fenetres PVC.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
              <p className="text-center text-gray-600">Exemples de réalisations modernes avec fenêtres PVC</p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Installation Professionnelle en Île-de-France</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres en PVC</strong> nécessite un savoir-faire professionnel pour garantir une performance optimale. Nos équipes d'artisans qualifiés interviennent dans toute l'Île-de-France pour réaliser votre projet selon les règles de l'art. Chaque <strong>installation de fenêtres PVC</strong> respecte les normes DTU 36.5 et fait l'objet d'une garantie décennale.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Personnalisation et Design</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>fenêtres en PVC</strong> se déclinent en multiples coloris et finitions pour s'adapter parfaitement à votre architecture. Du blanc classique aux teintes contemporaines, en passant par les imitations bois, chaque <strong>fenêtre PVC</strong> peut être personnalisée selon vos goûts et les contraintes urbanistiques de votre commune en Île-de-France.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les formes disponibles pour vos <strong>fenêtres PVC</strong> incluent les ouvertures classiques à la française, oscillo-battantes, coulissantes, ou encore les formes cintrées pour les projets architecturaux spécifiques.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Sécurité et Confort</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              La sécurité est au cœur de la conception de nos <strong>fenêtres en PVC</strong>. Équipées de systèmes de fermeture multipoints et de vitrages sécurisés, elles offrent une protection efficace contre les tentatives d'effraction. Les joints d'étanchéité garantissent une isolation acoustique remarquable, créant un environnement paisible à l'intérieur de votre habitation.
            </p>

            <div className="bg-blue-50 p-6 rounded-2xl my-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">💡 Le Saviez-vous ?</h4>
              <p className="text-gray-700">
                Les <strong>fenêtres PVC</strong> modernes intègrent des technologies avancées comme les vitrages autonettoyants ou les systèmes de ventilation intégrés pour améliorer la qualité de l'air intérieur.
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Financement et Aides</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres en PVC</strong> peut bénéficier de diverses aides financières : MaPrimeRénov', éco-PTZ, TVA réduite à 5,5%. En tant que professionnel RGE, Fenêtre & Co. vous accompagne dans vos démarches pour optimiser le financement de votre projet de <strong>fenêtres PVC</strong>.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Notre équipe vous fournit tous les documents nécessaires pour constituer vos dossiers de demande d'aides. L'investissement dans des <strong>fenêtres PVC</strong> performantes est rapidement amorti grâce aux économies d'énergie réalisées et aux aides publiques disponibles.
            </p>
          </article>

          {/* CTA Section */}
          <div className="bg-yellow-300 rounded-2xl p-8 mt-12 text-center">
            <h3 className="text-2xl font-bold text-black mb-4">
              Prêt à Installer vos Fenêtres en PVC ?
            </h3>
            <p className="text-black mb-6 text-lg">
              Demandez votre devis gratuit et bénéficiez de notre expertise de 35 ans dans l'installation de fenêtres PVC en Île-de-France.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Devis Gratuit Fenêtres PVC
              </Link>
              <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4">
                Appel Gratuit
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Fenêtres Aluminium Page Component
const FenetresAluminium = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-100 to-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Fenêtres en Aluminium : Design et Modernité
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Optez pour l'élégance et la performance avec nos fenêtres en aluminium. Design contemporain, grandes ouvertures et durabilité exceptionnelle pour votre projet en Île-de-France.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                  Devis Gratuit
                </Link>
                <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4">
                  📞 07 56 93 03 67
                </a>
              </div>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              {/* Vidéo Fenêtres Aluminium */}
              <video 
                className="w-full h-auto rounded-lg" 
                autoPlay 
                muted 
                loop 
                playsInline
                poster="/services/10.png"
              >
                <source src="/realisations/fenetres 3d.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Article SEO */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Fenêtres en Aluminium : L'Excellence Moderne</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres en aluminium</strong> incarnent parfaitement l'architecture contemporaine et les exigences modernes en matière de performance énergétique. Chez Fenêtre & Co., expert en <strong>installation de fenêtres aluminium</strong> en Île-de-France, nous mettons notre savoir-faire au service de vos projets les plus ambitieux, qu'il s'agisse de construction neuve ou de rénovation haut de gamme.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Les Atouts Uniques de l'Aluminium</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres en aluminium</strong> offre des possibilités architecturales incomparables. Ce matériau noble permet la création de grandes ouvertures avec des profilés fins, maximisant ainsi les apports lumineux naturels. Les <strong>fenêtres aluminium</strong> se distinguent par leur résistance mécanique exceptionnelle et leur capacité à supporter des vitrages de grandes dimensions sans déformation.
            </p>

            <div className="bg-yellow-300 p-6 rounded-2xl my-8">
              <h4 className="text-xl font-bold text-black mb-4">🏗️ Grandes Ouvertures Possibles</h4>
              <p className="text-black">
                Nos <strong>fenêtres aluminium</strong> permettent de réaliser des ouvertures jusqu'à 6 mètres de largeur, créant une continuité parfaite entre intérieur et extérieur.
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Performance Thermique de l'Aluminium Moderne</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Contrairement aux anciens modèles, les <strong>fenêtres en aluminium</strong> contemporaines intègrent une rupture de pont thermique qui garantit d'excellentes performances d'isolation. Nos <strong>fenêtres aluminium</strong> atteignent des coefficients thermiques Uw de 1,4 W/m².K, répondant largement aux exigences de la RT 2012 et anticipant celles de la RE 2020.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              La technologie de rupture de pont thermique utilisée dans nos <strong>fenêtres aluminium</strong> consiste en l'insertion d'un profilé isolant en polyamide entre les faces intérieure et extérieure du cadre. Cette innovation technique permet aux <strong>fenêtres en aluminium</strong> de rivaliser avec les performances des autres matériaux tout en conservant leurs avantages esthétiques et mécaniques.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Design et Esthétique Contemporaine</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres en aluminium</strong> offrent une liberté de design incomparable. Leur finesse permet de créer des lignes épurées particulièrement appréciées dans l'architecture moderne. Les <strong>fenêtres aluminium</strong> se déclinent en une palette de couleurs quasi illimitée grâce aux techniques de laquage et d'anodisation.
            </p>

            <div className="bg-gray-50 p-6 rounded-2xl my-8">
              {/* Vidéo design Fenêtres Aluminium */}
              <video 
                className="w-full h-auto rounded-lg mb-4" 
                autoPlay
                muted
                loop
                playsInline
                poster="/services/10.png"
              >
                <source src="/realisations/Fenetres Alluminium.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
              <p className="text-center text-gray-600">Exemples de réalisations modernes avec fenêtres aluminium</p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Durabilité et Résistance</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'aluminium est naturellement résistant à la corrosion grâce à la couche d'oxyde qui se forme spontanément à sa surface. Nos <strong>fenêtres aluminium</strong> conservent leurs propriétés mécaniques et esthétiques pendant plusieurs décennies sans nécessiter de traitement particulier. Cette durabilité exceptionnelle fait des <strong>fenêtres en aluminium</strong> un investissement pérenne pour votre habitation.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              La résistance aux intempéries des <strong>fenêtres aluminium</strong> est particulièrement appréciée en Île-de-France où les variations climatiques peuvent être importantes. Contrairement à d'autres matériaux, l'aluminium ne se dilate que très peu et conserve une stabilité dimensionnelle parfaite.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Installation Expertise en Île-de-France</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres aluminium</strong> demande une expertise particulière en raison de leur poids et de leurs dimensions souvent importantes. Nos équipes spécialisées maîtrisent parfaitement les techniques de pose spécifiques aux <strong>fenêtres en aluminium</strong>, garantissant une étanchéité parfaite et une durabilité optimale.
            </p>

            <div className="bg-blue-50 p-6 rounded-2xl my-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">🔧 Savoir-faire Technique</h4>
              <p className="text-gray-700">
                Chaque <strong>installation de fenêtres aluminium</strong> fait l'objet d'une étude technique préalable pour optimiser les performances et la longévité de vos équipements.
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Sécurité Renforcée</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres en aluminium</strong> offrent un niveau de sécurité élevé grâce à la robustesse intrinsèque du matériau. Les systèmes de fermeture multipoints équipant nos <strong>fenêtres aluminium</strong> assurent une protection optimale contre les tentatives d'effraction. La rigidité de l'aluminium permet également l'installation de vitrages de sécurité renforcée.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Écologie et Recyclabilité</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'aluminium est un matériau 100% recyclable à l'infini sans perte de ses qualités. Choisir des <strong>fenêtres aluminium</strong> s'inscrit dans une démarche écologique responsable. De plus, la longue durée de vie des <strong>fenêtres en aluminium</strong> contribue à réduire l'impact environnemental sur le long terme.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Personnalisation Avancée</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>fenêtres aluminium</strong> se personnalisent selon vos souhaits : couleurs, textures, systèmes d'ouverture, forme des ouvrants. Les possibilités de bicoloration permettent d'adapter parfaitement vos <strong>fenêtres en aluminium</strong> à votre architecture intérieure et extérieure. Cette flexibilité de personnalisation fait des <strong>fenêtres aluminium</strong> le choix privilégié des architectes et des projets sur-mesure.
            </p>
          </article>

          {/* CTA Section */}
          <div className="bg-yellow-300 rounded-2xl p-8 mt-12 text-center">
            <h3 className="text-2xl font-bold text-black mb-4">
              Découvrez nos Fenêtres en Aluminium
            </h3>
            <p className="text-black mb-6 text-lg">
              Contactez nos experts pour un projet sur-mesure de fenêtres aluminium en Île-de-France.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Devis Fenêtres Aluminium
              </Link>
              <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4">
                Conseil Expert
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Fenêtres Bois Page Component
const FenetresBois = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-100 to-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Fenêtres en Bois : Charme et Authenticité
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Redécouvrez le charme intemporel du bois avec nos fenêtres traditionnelles et modernes. Isolation naturelle, esthétique unique et durabilité pour votre habitat en Île-de-France.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                  Devis Gratuit
                </Link>
                <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4">
                  📞 07 56 93 03 67
                </a>
              </div>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              {/* Vidéo Fenêtres Bois */}
              <video 
                className="w-full h-auto rounded-lg" 
                autoPlay 
                muted 
                loop 
                playsInline
                poster="/services/11.png"
              >
                <source src="/realisations/fenetres 3d.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Article SEO */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Fenêtres en Bois : Tradition et Performance</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres en bois</strong> représentent l'alliance parfaite entre tradition et modernité. Chez Fenêtre & Co., spécialiste de l'<strong>installation de fenêtres bois</strong> en Île-de-France, nous perpétuons l'art ancestral de la menuiserie tout en intégrant les technologies contemporaines pour vous offrir des <strong>fenêtres bois</strong> aux performances exceptionnelles.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Le Bois : Un Matériau Noble et Vivant</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres en bois</strong> apporte une dimension chaleureuse et authentique à votre habitat. Matériau naturel par excellence, le bois possède des qualités intrinsèques uniques : isolation thermique naturelle, régulation hygrométrique et esthétique intemporelle. Nos <strong>fenêtres bois</strong> sont fabriquées à partir d'essences soigneusement sélectionnées pour leur durabilité et leurs performances.
            </p>

            <div className="bg-yellow-300 p-6 rounded-2xl my-8">
              <h4 className="text-xl font-bold text-black mb-4">🌳 Isolation Naturelle Exceptionnelle</h4>
              <p className="text-black">
                Les <strong>fenêtres bois</strong> offrent naturellement une excellente isolation thermique grâce à la structure cellulaire du bois qui emprisonne l'air et limite les transferts thermiques.
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Essences de Bois et Performances</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>fenêtres en bois</strong> utilisent différentes essences en fonction de vos besoins et de votre budget. Le pin sylvestre, essence européenne robuste, constitue un excellent rapport qualité-prix pour vos <strong>fenêtres bois</strong>. Le chêne, symbole de noblesse et de longévité, convient parfaitement aux projets patrimoniaux et aux rénovations de caractère.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les bois exotiques comme le moabi ou l'iroko apportent une résistance naturelle exceptionnelle aux intempéries pour vos <strong>fenêtres bois</strong> exposées. Ces essences tropicales ne nécessitent aucun traitement chimique et conservent leurs propriétés pendant des décennies. L'<strong>installation de fenêtres en bois</strong> exotique constitue un investissement durable pour votre patrimoine.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Traitement et Protection du Bois</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>fenêtres bois</strong> bénéficient de traitements de protection avancés qui garantissent leur pérennité. Le traitement en autoclave protège le bois contre les insectes xylophages et les champignons. Les lasures et peintures microporeuses permettent au bois de respirer tout en le protégeant des UV et de l'humidité.
            </p>

            <div className="bg-gray-50 p-6 rounded-2xl my-8">
              {/* Vidéo réalisations Fenêtres Bois */}
              <video 
                className="w-full h-auto rounded-lg mb-4" 
                autoPlay
                muted
                loop
                playsInline
                poster="/services/11.png"
              >
                <source src="/realisations/Fenetres en bois.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
              <p className="text-center text-gray-600">Exemples de réalisations modernes avec fenêtres en bois</p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Performance Énergétique Moderne</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres en bois</strong> contemporaines atteignent d'excellentes performances énergétiques. Nos <strong>fenêtres bois</strong> équipées de double vitrage à isolation renforcée affichent des coefficients Uw inférieurs à 1,4 W/m².K. Cette performance, alliée aux propriétés naturelles du bois, génère des économies d'énergie significatives.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres bois</strong> contribue à améliorer le confort thermique été comme hiver. La masse thermique du bois régule naturellement les variations de température, créant une ambiance intérieure stable et confortable. Cette régulation naturelle distingue les <strong>fenêtres en bois</strong> des autres matériaux.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Esthétique et Personnalisation</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres bois</strong> offrent des possibilités de personnalisation infinies. Chaque essence possède ses nuances et son grain unique, permettant de créer des <strong>fenêtres en bois</strong> parfaitement adaptées à votre style architectural. Les finitions peuvent être transparentes pour révéler la beauté naturelle du bois ou opaques pour s'harmoniser avec votre façade.
            </p>

            <div className="bg-blue-50 p-6 rounded-2xl my-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">🎨 Finitions Sur-Mesure</h4>
              <p className="text-gray-700">
                Nos <strong>fenêtres bois</strong> se déclinent en centaines de teintes et finitions pour s'intégrer parfaitement à votre projet architectural.
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Installation Traditionnelle et Moderne</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres en bois</strong> requiert un savoir-faire traditionnel que nos artisans maîtrisent parfaitement. Chaque <strong>fenêtre bois</strong> est posée selon les règles de l'art, en respectant les techniques ancestrales adaptées aux contraintes modernes. Cette expertise garantit la longévité et les performances de vos <strong>fenêtres bois</strong>.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Entretien et Durabilité</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Contrairement aux idées reçues, l'entretien des <strong>fenêtres bois</strong> modernes est simple et espacé dans le temps. Un entretien tous les 5 à 8 ans suffit pour conserver l'esthétique et les performances de vos <strong>fenêtres en bois</strong>. Cet entretien consiste principalement en un ponçage léger et l'application d'une nouvelle couche de protection.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Écologie et Développement Durable</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>fenêtres bois</strong> s'inscrivent parfaitement dans une démarche écologique. Le bois est un matériau renouvelable qui stocke le carbone pendant toute sa durée de vie. Nos <strong>fenêtres en bois</strong> proviennent de forêts gérées durablement, certifiées PEFC ou FSC. Choisir des <strong>fenêtres bois</strong> contribue à la préservation de l'environnement.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Rénovation du Patrimoine</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de fenêtres bois</strong> est particulièrement adaptée à la rénovation du patrimoine architectural. Nos <strong>fenêtres en bois</strong> peuvent reproduire fidèlement les profils anciens tout en intégrant les performances énergétiques modernes. Cette approche respectueuse permet de concilier préservation patrimoniale et confort contemporain.
            </p>
          </article>

          {/* CTA Section */}
          <div className="bg-yellow-300 rounded-2xl p-8 mt-12 text-center">
            <h3 className="text-2xl font-bold text-black mb-4">
              Choisissez l'Élégance du Bois
            </h3>
            <p className="text-black mb-6 text-lg">
              Nos artisans experts vous accompagnent dans votre projet de fenêtres bois sur-mesure en Île-de-France.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Devis Fenêtres Bois
              </Link>
              <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4">
                Expertise Gratuite
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Baies Vitrées Page Component
const BaiesVitrees = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-100 to-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Baies Vitrées : Lumière et Ouverture
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Transformez votre espace de vie avec nos baies vitrées sur-mesure. Maximisez la lumière naturelle et créez une continuité parfaite entre intérieur et extérieur.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                  Devis Gratuit
                </Link>
                <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4">
                  📞 07 56 93 03 67
                </a>
              </div>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              {/* Vidéo Baies Vitrées */}
              <video 
                className="w-full h-auto rounded-lg" 
                autoPlay 
                muted 
                loop 
                playsInline
                poster="/realisations/14.png"
              >
                <source src="/realisations/fenetres 3d.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Article SEO */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Baies Vitrées : Révolutionnez votre Habitat</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>baies vitrées</strong> transforment radicalement votre espace de vie en créant une ouverture généreuse sur l'extérieur. Chez Fenêtre & Co., expert en <strong>installation de baies vitrées</strong> en Île-de-France, nous concevons et installons des <strong>baies vitrées sur-mesure</strong> qui subliment votre architecture et améliorent votre qualité de vie au quotidien.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Maximiser la Lumière Naturelle</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de baies vitrées</strong> constitue la solution la plus efficace pour augmenter considérablement les apports lumineux dans votre habitation. Nos <strong>baies vitrées</strong> peuvent atteindre des dimensions impressionnantes, créant de véritables murs de lumière qui transforment l'ambiance de vos pièces à vivre. Cette luminosité accrue améliore le bien-être et réduit les besoins en éclairage artificiel.
            </p>

            <div className="bg-yellow-300 p-6 rounded-2xl my-8">
              <h4 className="text-xl font-bold text-black mb-4">☀️ Gain de Luminosité Exceptionnel</h4>
              <p className="text-black">
                Nos <strong>baies vitrées</strong> peuvent augmenter la luminosité naturelle de votre pièce jusqu'à 300%, créant un environnement plus sain et plus agréable.
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Types de Baies Vitrées</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>baies vitrées</strong> se déclinent en plusieurs systèmes d'ouverture adaptés à vos besoins et à votre architecture. Les <strong>baies vitrées coulissantes</strong> offrent un gain de place optimal et permettent de créer de très grandes ouvertures. Le système coulissant à galandage fait disparaître totalement les vantaux dans la cloison pour une ouverture intégrale.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>baies vitrées</strong> à ouvrant traditionnel conviennent parfaitement aux projets de rénovation où l'espace d'ouverture n'est pas contraint. Les systèmes pliants, quant à eux, permettent de créer des ouvertures panoramiques spectaculaires en repliant les vantaux en accordéon. Chaque type de <strong>baie vitrée</strong> offre ses avantages spécifiques selon votre projet.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Performance Énergétique</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>baies vitrées</strong> intègrent les technologies les plus avancées en matière d'isolation thermique. Les vitrages à isolation renforcée (VIR) équipant nos <strong>baies vitrées</strong> permettent d'atteindre des coefficients thermiques Uw de 1,1 W/m².K. Cette performance garantit un confort optimal été comme hiver tout en préservant vos économies d'énergie.
            </p>

            <div className="bg-gray-50 p-6 rounded-2xl my-8">
              {/* Vidéo réalisations Baies Vitrées */}
              <video 
                className="w-full h-auto rounded-lg mb-4" 
                autoPlay
                muted
                loop
                playsInline
                poster="/realisations/14.png"
              >
                <source src="/realisations/baies vitres.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
              <p className="text-center text-gray-600">Exemples de réalisations modernes de Baies vitres</p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Matériaux et Finitions</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>baies vitrées</strong> sont disponibles dans tous les matériaux : PVC pour un excellent rapport qualité-prix, aluminium pour un design contemporain, ou bois pour un charme authentique. Les <strong>baies vitrées mixtes</strong> combinent les avantages de deux matériaux, par exemple bois côté intérieur pour l'esthétique et aluminium côté extérieur pour la résistance.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les finitions de nos <strong>baies vitrées</strong> s'adaptent parfaitement à votre architecture. La gamme de coloris disponible permet d'harmoniser vos <strong>baies vitrées</strong> avec l'ensemble de votre façade. Les options de bicoloration offrent encore plus de possibilités de personnalisation pour vos <strong>baies vitrées sur-mesure</strong>.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Sécurité et Protection</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              La sécurité de nos <strong>baies vitrées</strong> fait l'objet d'une attention particulière. Les vitrages feuilletés équipant nos <strong>baies vitrées</strong> offrent une résistance à l'effraction tout en conservant leurs qualités esthétiques. Les systèmes de fermeture multipoints garantissent une sécurité optimale sur toute la hauteur de vos <strong>baies vitrées</strong>.
            </p>

            <div className="bg-blue-50 p-6 rounded-2xl my-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">🔒 Sécurité Renforcée</h4>
              <p className="text-gray-700">
                Nos <strong>baies vitrées</strong> intègrent des systèmes de sécurité avancés : vitrages anti-effraction, serrures multipoints et capteurs d'ouverture.
              </p>
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Installation Technique Spécialisée</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de baies vitrées</strong> nécessite une expertise technique pointue en raison des dimensions importantes et du poids considérable de ces équipements. Nos équipes spécialisées maîtrisent parfaitement les techniques de pose spécifiques aux <strong>baies vitrées</strong>, incluant la création d'ouvertures, le renforcement des structures et l'étanchéité parfaite.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Chaque <strong>installation de baies vitrées</strong> fait l'objet d'une étude technique préalable pour vérifier la faisabilité structurelle et dimensionner les renforts nécessaires. Cette approche professionnelle garantit la pérennité et la sécurité de vos <strong>baies vitrées</strong> dans le temps.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Aménagement et Décoration</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Les <strong>baies vitrées</strong> révolutionnent l'aménagement intérieur en créant une continuité visuelle avec l'extérieur. Cette ouverture sur le jardin ou la terrasse agrandit visuellement l'espace et permet de repenser complètement la décoration intérieure. Nos <strong>baies vitrées</strong> deviennent le point focal de votre pièce à vivre.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Confort Acoustique</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>baies vitrées</strong> intègrent des vitrages acoustiques performants pour préserver votre tranquillité. Cette caractéristique est particulièrement appréciée en Île-de-France où l'environnement urbain peut être bruyant. L'isolation acoustique de nos <strong>baies vitrées</strong> peut atteindre 40 dB de réduction sonore.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Réglementation et Normes</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              L'<strong>installation de baies vitrées</strong> doit respecter différentes réglementations : PLU communal, règles de copropriété, normes de sécurité. Notre équipe vous accompagne dans toutes ces démarches administratives et techniques. Nous nous assurons que vos <strong>baies vitrées</strong> respectent parfaitement la réglementation en vigueur.
            </p>

            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Motorisation et Domotique</h3>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Nos <strong>baies vitrées</strong> peuvent être équipées de systèmes de motorisation pour un confort d'utilisation optimal. La domotique permet de piloter vos <strong>baies vitrées</strong> à distance et de les intégrer dans votre système de maison connectée. Ces solutions technologiques ajoutent une dimension moderne et pratique à vos <strong>baies vitrées</strong>.
            </p>
          </article>

          {/* CTA Section */}
          <div className="bg-yellow-300 rounded-2xl p-8 mt-12 text-center">
            <h3 className="text-2xl font-bold text-black mb-4">
              Ouvrez-vous à la Lumière
            </h3>
            <p className="text-black mb-6 text-lg">
              Transformez votre habitat avec nos baies vitrées sur-mesure. Expertise et installation professionnelle en Île-de-France.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Projet Baies Vitrées
              </Link>
              <a href="tel:0756930367" className="btn-secondary text-lg px-8 py-4">
                Visite Technique Gratuite
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Services Page Component
const Services = () => {
  const services = [
    {
      title: "Fenêtres en PVC",
      description: "Performance thermique et acoustique optimale avec un excellent rapport qualité-prix",
      features: ["Isolation thermique renforcée", "Entretien minimal", "Durabilité garantie", "Large choix de couleurs"],
      image: "/services/9.png",
      link: "/services/fenetres-pvc"
    },
    {
      title: "Fenêtres en Aluminium",
      description: "Esthétique moderne et design contemporain pour votre habitat",
      features: ["Design épuré", "Très haute résistance", "Finitions premium", "Grandes dimensions possibles"],
      image: "/services/10.png",
      link: "/services/fenetres-aluminium"
    },
    {
      title: "Fenêtres en Bois",
      description: "Charme authentique et isolation naturelle pour un style intemporel",
      features: ["Matériau noble", "Isolation naturelle", "Personnalisation totale", "Écologique"],
      image: "/services/11.png",
      link: "/services/fenetres-bois"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-100 to-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Nos fenêtres sur mesure
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez notre gamme complète de fenêtres adaptées à tous vos besoins. 
            Nos artisans experts vous accompagnent dans le choix de la solution idéale pour votre habitat.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div 
                  className="h-64 bg-cover bg-center rounded-t-2xl"
                  style={{ backgroundImage: `url(${service.image})` }}
                ></div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <span className="text-yellow-500 mr-2">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link} className="btn-primary w-full text-center">
                    En savoir plus
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Services complémentaires
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="additional-service-card">
              <div className="text-4xl mb-4">🏠</div>
              <h3 className="text-lg font-semibold mb-2">Fenêtres mixtes</h3>
              <p className="text-gray-600 mb-4">Combinaison bois/aluminium pour allier performance et esthétique</p>
              <Link to="/contact" className="text-yellow-500 font-semibold hover:text-yellow-600">
                Devis →
              </Link>
            </div>
            
            <div className="additional-service-card">
              <div className="text-4xl mb-4">🪟</div>
              <h3 className="text-lg font-semibold mb-2">Baies vitrées</h3>
              <p className="text-gray-600 mb-4">Ouvertures panoramiques pour plus de luminosité</p>
              <Link to="/services/baies-vitrees" className="text-yellow-500 font-semibold hover:text-yellow-600">
                En savoir plus →
              </Link>
            </div>
            
            <div className="additional-service-card">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Oscillo-battantes</h3>
              <p className="text-gray-600 mb-4">Ouverture double pour une ventilation optimale</p>
              <Link to="/contact" className="text-yellow-500 font-semibold hover:text-yellow-600">
                Devis →
              </Link>
            </div>
            
            <div className="additional-service-card">
              <div className="text-4xl mb-4">📏</div>
              <h3 className="text-lg font-semibold mb-2">Sur-mesure</h3>
              <p className="text-gray-600 mb-4">Fabrication adaptée à vos dimensions spécifiques</p>
              <Link to="/contact" className="text-yellow-500 font-semibold hover:text-yellow-600">
                Devis →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos réalisations récentes
            </h2>
            <p className="text-xl text-gray-600">
              Découvrez quelques-unes de nos installations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="gallery-item">
              <img 
                src="/realisations/12.png" 
                alt="Fenêtres PVC - Maison individuelle"
                className="w-full h-64 object-cover rounded-lg"
              />
              <div className="mt-4">
                <h4 className="font-semibold text-gray-900">Fenêtres PVC - Maison individuelle</h4>
                <p className="text-gray-600">Vincennes (94)</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <img 
                src="/realisations/13.png" 
                alt="Fenêtres bois - Appartement"
                className="w-full h-64 object-cover rounded-lg"
              />
              <div className="mt-4">
                <h4 className="font-semibold text-gray-900">Fenêtres bois - Appartement</h4>
                <p className="text-gray-600">Créteil (94)</p>
              </div>
            </div>
            
            <div className="gallery-item">
              <img 
                src="/realisations/14.png" 
                alt="Baie vitrée aluminium"
                className="w-full h-64 object-cover rounded-lg"
              />
              <div className="mt-4">
                <h4 className="font-semibold text-gray-900">Baie vitrée aluminium</h4>
                <p className="text-gray-600">Nogent-sur-Marne (94)</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Partnership Page Component
const Partnership = () => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Préparer les données pour Zapier - Page Partenariat
    const zapierData = {
      type: "Candidature Partenaire",
      company: formData.company,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      timestamp: new Date().toISOString(),
      source: "Page Partenariat"
    };

    console.log('=== ENVOI ZAPIER PARTENARIAT ===');
    console.log('Données:', zapierData);

    // Méthode simple et efficace : URL avec paramètres
    const zapierUrl = 'https://hooks.zapier.com/hooks/catch/7451293/u214sam/?' + 
      Object.entries(zapierData)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');

    console.log('URL finale:', zapierUrl);

    // Envoyer via une image invisible (100% fiable)
    const img = new Image();
    img.onload = () => console.log('✅ Zapier SUCCESS');
    img.onerror = () => console.log('❌ Zapier ERROR');
    img.src = zapierUrl;
    
    // Essayer aussi via fetch (bonus)
    fetch(zapierUrl, { mode: 'no-cors' })
      .then(() => console.log('✅ Fetch SUCCESS'))
      .catch(() => console.log('❌ Fetch ERROR'));

    alert('Merci pour votre candidature ! Nous vous recontacterons rapidement.');
    setFormData({
      company: '',
      name: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-100 to-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Devenez partenaire
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Gagnez en recommandant nos services !
          </p>
        </div>
      </section>

      {/* Partnership Info with Image */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Un partenariat gagnant-gagnant
              </h2>
              <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-2xl p-8 mb-8">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Vous êtes <strong>apporteur d'affaires</strong>, <strong>régie</strong> ou vous connaissez des clients ayant un projet de fenêtres ?
                  <br/><br/>
                  Nous rémunérons chaque recommandation concrétisée par un <strong className="text-yellow-600">pourcentage attractif</strong> sur chaque vente réalisée grâce à vous.
                </p>
              </div>
              
              {/* Benefits */}
              <div className="grid grid-cols-1 gap-6">
                <div className="flex items-start">
                  <div className="text-3xl mr-4">💰</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Rémunération attractive</h3>
                    <p className="text-gray-600">Commissions compétitives sur chaque vente finalisée</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="text-3xl mr-4">🤝</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Partenariat durable</h3>
                    <p className="text-gray-600">Collaboration à long terme avec suivi personnalisé</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="text-3xl mr-4">📈</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Support commercial</h3>
                    <p className="text-gray-600">Outils et support pour optimiser vos recommandations</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/regie/puzzle.png" 
                  alt="Partenariat puzzle"
                  className="w-full h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Form */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-300 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-black mb-6 text-center">
              Formulaire de candidature partenaire
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label className="form-label-dark">Société / Activité *</label>
                  <input 
                    type="text" 
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Nom de votre entreprise"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label-dark">Nom et Prénom *</label>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Votre nom complet"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label-dark">Email *</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="votre@email.com"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label-dark">Téléphone *</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label-dark">Message</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="form-input"
                  placeholder="Parlez-nous de votre activité et de vos clients potentiels..."
                ></textarea>
              </div>
              
              <button type="submit" className="btn-primary w-full text-lg py-4">
                Envoyer ma candidature
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page Component
const About = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-100 to-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Une histoire de famille
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Trois générations de passion pour l'artisanat
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Depuis 1989, l'excellence familiale
              </h2>
              <div className="prose prose-lg text-gray-700">
                <p className="mb-6">
                  Notre entreprise familiale est spécialisée dans la pose de fenêtres depuis <strong>1989</strong>. 
                  Tout a commencé avec notre grand-père, un artisan passionné qui a transmis son savoir-faire à son fils.
                </p>
                <p className="mb-6">
                  Aujourd'hui, la relève continue avec le petit-fils, perpétuant cette tradition d'excellence 
                  et d'innovation dans le respect des valeurs familiales.
                </p>
                <p className="mb-6">
                  Nous mettons l'accent sur la <strong>qualité</strong>, le <strong>sérieux</strong>, 
                  la <strong>proximité</strong> et la <strong>satisfaction</strong> de chaque client.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/a propos/pere en fils.png" 
                  alt="Équipe familiale d'artisans"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-yellow-300 text-black p-6 rounded-2xl">
                <div className="text-center">
                  <div className="text-3xl font-bold">35+</div>
                  <div className="text-sm">Années d'expérience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos valeurs
            </h2>
            <p className="text-xl text-gray-600">
              Ce qui nous guide au quotidien
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="value-card">
              <div className="text-4xl mb-4">🔧</div>
              <h3 className="text-xl font-semibold mb-2">Professionnalisme</h3>
              <p className="text-gray-600">
                Expertise technique et respect des normes les plus strictes
              </p>
            </div>
            
            <div className="value-card">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="text-xl font-semibold mb-2">Confiance</h3>
              <p className="text-gray-600">
                Transparence et honnêteté dans toutes nos relations clients
              </p>
            </div>
            
            <div className="value-card">
              <div className="text-4xl mb-4">⭐</div>
              <h3 className="text-xl font-semibold mb-2">Qualité artisanale</h3>
              <p className="text-gray-600">
                Savoir-faire traditionnel allié aux techniques modernes
              </p>
            </div>
            
            <div className="value-card">
              <div className="text-4xl mb-4">❤️</div>
              <h3 className="text-xl font-semibold mb-2">Relation humaine</h3>
              <p className="text-gray-600">
                Écoute, proximité et accompagnement personnalisé
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre parcours
            </h2>
          </div>
          
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-year">1989</div>
              <div className="timeline-content">
                <h3 className="text-xl font-semibold mb-2">Les débuts</h3>
                <p className="text-gray-600">
                  Création de l'entreprise par le grand-père, spécialisation dans la menuiserie traditionnelle
                </p>
              </div>
            </div>
            
            <div className="timeline-item">
              <div className="timeline-year">2000</div>
              <div className="timeline-content">
                <h3 className="text-xl font-semibold mb-2">Transmission</h3>
                <p className="text-gray-600">
                  Le fils reprend l'entreprise et modernise les techniques de pose
                </p>
              </div>
            </div>
            
            <div className="timeline-item">
              <div className="timeline-year">2015</div>
              <div className="timeline-content">
                <h3 className="text-xl font-semibold mb-2">Innovation</h3>
                <p className="text-gray-600">
                  Introduction des fenêtres haute performance énergétique
                </p>
              </div>
            </div>
            
            <div className="timeline-item">
              <div className="timeline-year">2024</div>
              <div className="timeline-content">
                <h3 className="text-xl font-semibold mb-2">Nouvelle génération</h3>
                <p className="text-gray-600">
                  Le petit-fils rejoint l'entreprise, apportant digitalisation et nouvelles technologies
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Contact Page Component
const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Préparer les données pour Zapier
    const zapierData = {
      type: "Contact",
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      projectType: "", // Pas de type de projet sur la page contact
      message: formData.message,
      timestamp: new Date().toISOString(),
      source: "Page de contact"
    };

    console.log('Envoi vers Zapier:', zapierData);

    // Méthode 1: Via une image invisible (toujours fonctionnel)
    const zapierUrl = 'https://hooks.zapier.com/hooks/catch/7451293/u2mcxu6/?' + 
      new URLSearchParams({
        type: zapierData.type,
        firstName: zapierData.firstName,
        lastName: zapierData.lastName,
        phone: zapierData.phone,
        email: zapierData.email,
        projectType: zapierData.projectType,
        message: zapierData.message,
        timestamp: zapierData.timestamp,
        source: zapierData.source
      }).toString();

    // Créer une image invisible pour déclencher la requête
    const img = new Image();
    img.src = zapierUrl;
    
    console.log('URL Zapier:', zapierUrl);

    // Méthode 2: Via le backend (backup)
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zapier-webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(zapierData),
      });

      if (response.ok) {
        console.log('Succès via backend');
      } else {
        console.log('Échec backend, mais image envoyée');
      }
    } catch (error) {
      console.log('Erreur backend, mais image envoyée:', error);
    }

    alert('Merci pour votre message ! Nous vous répondrons sous 24h.');
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-100 to-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Besoin d'un conseil ou d'un devis ? Écrivez-nous, réponse sous 24h
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Envoyez-nous un message
              </h2>
              
              <div className="bg-yellow-300 rounded-2xl p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label-dark">Prénom *</label>
                      <input 
                        type="text" 
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label-dark">Nom *</label>
                      <input 
                        type="text" 
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label-dark">Téléphone *</label>
                      <input 
                        type="tel" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label-dark">Email *</label>
                      <input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label-dark">Message *</label>
                    <textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="6"
                      required
                      className="form-input"
                      placeholder="Décrivez votre projet ou posez-nous vos questions..."
                    ></textarea>
                  </div>
                  
                  <button type="submit" className="btn-primary w-full text-lg py-4">
                    Envoyer le message
                  </button>
                </form>
              </div>
            </div>
            
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Nos coordonnées
              </h2>
              
              <div className="space-y-6">
                <div className="contact-info-card">
                  <div className="text-3xl mb-4">📞</div>
                  <h3 className="text-xl font-semibold mb-2">Téléphone</h3>
                  <p className="text-gray-600">07 56 93 03 67</p>
                  <p className="text-sm text-gray-500">Appel gratuit depuis un fixe</p>
                </div>
                
                <div className="contact-info-card">
                  <div className="text-3xl mb-4">📧</div>
                  <h3 className="text-xl font-semibold mb-2">Email</h3>
                  <p className="text-gray-600">rudyo@ddr-solutions.com</p>
                  <p className="text-sm text-gray-500">Réponse sous 24h</p>
                </div>
                
                <div className="contact-info-card">
                  <div className="text-3xl mb-4">🕒</div>
                  <h3 className="text-xl font-semibold mb-2">Horaires d'ouverture</h3>
                  <div className="text-gray-600">
                    <p>Lundi à vendredi : 09h00 – 20h00</p>
                    <p>Samedi et dimanche : Fermé</p>
                  </div>
                </div>
                
                <div className="contact-info-card">
                  <div className="text-3xl mb-4">📍</div>
                  <h3 className="text-xl font-semibold mb-2">Adresse</h3>
                  <p className="text-gray-600">
                    250 bis boulevard Jean Jaurès<br/>
                    92100 Boulogne Billancourt<br/>
                    France
                  </p>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-yellow-100 rounded-2xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  ✨ Devis gratuit
                </h3>
                <p className="text-gray-800">
                  Déplacement offert en Île-de-France et sa couronne
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Où nous trouver
            </h2>
            <p className="text-xl text-gray-600">
              Située à Boulogne Billancourt, notre entreprise intervient dans toute l'Île-de-France
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <GoogleMap />
            
            {/* Zones d'intervention */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-2xl mb-1">🏢</div>
                <div className="text-sm font-semibold">Paris (75)</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-2xl mb-1">🏘️</div>
                <div className="text-sm font-semibold">Hauts-de-Seine (92)</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-2xl mb-1">🏠</div>
                <div className="text-sm font-semibold">Yvelines (78)</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-2xl mb-1">🏡</div>
                <div className="text-sm font-semibold">Essonne (91)</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Navigation />
        <Breadcrumb />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/fenetres-pvc" element={<FenetresPVC />} />
          <Route path="/services/fenetres-aluminium" element={<FenetresAluminium />} />
          <Route path="/services/fenetres-bois" element={<FenetresBois />} />
          <Route path="/services/baies-vitrees" element={<BaiesVitrees />} />
          <Route path="/partenariat" element={<Partnership />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;